import { useState } from 'react';
import { AddressBook, EnvelopeSimple, LinkSimple, Phone, Plus, Trash, UserPlus } from '@phosphor-icons/react';
import { BestaetigenDialog, ImmoButton, InnenKarte, KartenTitel, LeerZustand, SearchSelect, ToastPill, useToastPill, type SucheOption } from '../../../../../components/immobilien/ui';
import { supabase } from '../../../../../lib/supabase/client';
import { initialen } from '../../../../../lib/immobilien/format';
import { useOpenKontakt } from '../../../../../lib/immobilien/openKontakt';
import { bereinigeSuchbegriff } from '../../../../../lib/kontakte/personenSuche';
import { kontaktErreichbarkeit, kontaktName, loeseObjektKontakt, verknuepfeObjektKontakt, type ObjektKontaktZeile } from '../../../../../lib/objekte/objektKontakte';
import { NeuerKontaktFormular } from './NeuerKontaktFormular';
import { useObjektSeite } from '../ObjektSeitenKontext';

/** Funktionen wie bisher beim Verknüpfen von Kontakten. */
const FUNKTIONEN = ['Hausmeister', 'Handwerker', 'Immobilienverwaltung', 'Makler', 'Bank', 'Dienstleister', 'Mieter', 'Eigentümer', 'Bekannter', 'Sonstige'];

/** Suche in kontakte – gleiche Abfrage wie bisher, höchstens 8 Treffer. */
async function sucheKontakte(anfrage: string, _typ: string | null, signal: AbortSignal): Promise<SucheOption[]> {
  const q = bereinigeSuchbegriff(anfrage);
  let abfrage = supabase.from('kontakte').select('id, vorname, nachname, firma_name, email, typ').limit(8);
  if (q) abfrage = abfrage.or(`vorname.ilike.%${q}%,nachname.ilike.%${q}%,firma_name.ilike.%${q}%,email.ilike.%${q}%`);
  const { data, error } = await abfrage.order('nachname').abortSignal(signal);
  if (error) throw new Error(error.message);
  return ((data ?? []) as { id: string; vorname: string | null; nachname: string | null; firma_name: string | null; email: string | null; typ: string | null }[])
    .map(k => ({ id: k.id, label: kontaktName(k), sub: [k.typ, k.email].filter(Boolean).join(' · ') || undefined }));
}

/** Panel „Kontakte“: am Objekt verknüpfte Kontakte und Eigentümer; verknüpfen, neu anlegen, lösen. */
export function KontaktePanel() {
  const { daten, neuLaden } = useObjektSeite();
  const openKontakt = useOpenKontakt();
  const [toast, zeige] = useToastPill();
  const [gewaehlt, setGewaehlt] = useState<SucheOption | null>(null);
  const [funktion, setFunktion] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [neuOffen, setNeuOffen] = useState(false);
  const [loesen, setLoesen] = useState<ObjektKontaktZeile | null>(null);
  const objektId = daten.objekt.id;

  const verknuepfen = async () => {
    if (!gewaehlt) return;
    if (daten.kontakte.some(k => k.target_id === gewaehlt.id)) { zeige(`${gewaehlt.label} ist bereits verknüpft`, true); return; }
    setLaeuft(true);
    try {
      await verknuepfeObjektKontakt(objektId, gewaehlt.id, funktion);
      zeige(`${gewaehlt.label} verknüpft`);
      setGewaehlt(null); setFunktion('');
      neuLaden('kontakte');
    } catch (e) {
      zeige(`Nicht verknüpft: ${e instanceof Error ? e.message : 'Unbekannt'}`, true);
    } finally {
      setLaeuft(false);
    }
  };

  const bestaetigeLoesen = async () => {
    if (!loesen) return;
    setLaeuft(true);
    try {
      await loeseObjektKontakt(loesen.link_id);
      zeige(`${kontaktName(loesen)} gelöst`);
      setLoesen(null);
      neuLaden('kontakte');
    } catch (e) {
      zeige(`Nicht gelöst: ${e instanceof Error ? e.message : 'Unbekannt'}`, true);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
        <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>Kontakte</span>
        <ImmoButton art={neuOffen ? 'fuellung' : 'akzent'} groesse={40} icon={neuOffen ? Plus : UserPlus} iconGedreht={neuOffen} aria-expanded={neuOffen}
          onClick={() => setNeuOffen(o => !o)}>{neuOffen ? 'Abbrechen' : 'Neuen Kontakt anlegen'}</ImmoButton>
      </div>

      {neuOffen && (
        <div className="immo-side-in">
          <NeuerKontaktFormular objektId={objektId} onAbbrechen={() => setNeuOffen(false)}
            onAngelegt={(meldung, fehler) => { setNeuOffen(false); zeige(meldung, fehler); neuLaden('kontakte'); }} />
        </div>
      )}

      {daten.kontakte.length === 0 ? (
        <LeerZustand icon={AddressBook} titel="Keine Kontakte verknüpft" text="Verknüpfe Hausmeister, Handwerker, Bank oder andere Ansprechpartner mit diesem Objekt." />
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 10 }}>
          {daten.kontakte.map(k => {
            const name = kontaktName(k);
            const telefon = k.telefon || k.handynummer || k.festnetz || null;
            const typ = k._source === 'eigentuemer' ? 'eigentuemer' : 'kontakt';
            return (
              <div key={k.link_id} className="immo-innen flex flex-col" style={{ borderRadius: 20, padding: 14, gap: 12 }}>
                <div className="flex items-center" style={{ gap: 12 }}>
                  <span className="flex items-center justify-center shrink-0" style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--acc-fill)', color: 'var(--acc-ink)', fontSize: 14, fontWeight: 700 }}>{initialen(name)}</span>
                  <button type="button" onClick={() => openKontakt(k.target_id, typ)} className="flex flex-col flex-1 min-w-0 text-left cursor-pointer" style={{ border: 'none', background: 'transparent', padding: 0, color: 'var(--ink)' }}>
                    <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{name}</span>
                    <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{k.funktion || (k._source === 'eigentuemer' ? 'Eigentümer' : 'Kontakt')}</span>
                  </button>
                  <button type="button" aria-label={`${name} vom Objekt lösen`} title="Verknüpfung lösen" onClick={() => setLoesen(k)}
                    className="flex items-center justify-center cursor-pointer" style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--ink3)' }}>
                    <Trash size={16} />
                  </button>
                </div>
                <div className="flex items-center" style={{ gap: 6 }}>
                  <span className="truncate flex-1" style={{ fontSize: 12, color: 'var(--ink2)' }}>{kontaktErreichbarkeit(k) || 'Keine Kontaktdaten'}</span>
                  {telefon && <a href={`tel:${telefon.replace(/\s+/g, '')}`} aria-label={`${name} anrufen`} className="flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: 'var(--ok-soft)', color: 'var(--ok)' }}><Phone weight="fill" size={15} /></a>}
                  {k.email && <a href={`mailto:${k.email}`} aria-label={`E-Mail an ${name}`} className="flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: 'var(--acc-soft)', color: 'var(--acc)' }}><EnvelopeSimple weight="fill" size={15} /></a>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <InnenKarte>
        <KartenTitel icon={LinkSimple}>Bestehenden Kontakt verknüpfen</KartenTitel>
        <div className="flex flex-wrap items-end" style={{ gap: 10 }}>
          <div style={{ flex: '2 1 260px' }}>
            <SearchSelect label="Kontakt" platzhalter="Name, Firma oder E-Mail …" suche={sucheKontakte} onWahl={setGewaehlt} ausgewaehlt={gewaehlt} onEntfernen={() => setGewaehlt(null)} />
          </div>
          <div className="flex flex-col" style={{ flex: '1 1 180px', gap: 6 }}>
            <label htmlFor="kontakt-funktion" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Funktion</label>
            <input id="kontakt-funktion" list="kontakt-funktionen" value={funktion} onChange={e => setFunktion(e.target.value)} placeholder="z. B. Hausmeister"
              style={{ height: 50, borderRadius: 15, padding: '0 14px', border: 'none', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 15, fontWeight: 700, color: 'var(--ink)' }} />
            <datalist id="kontakt-funktionen">{FUNKTIONEN.map(f => <option key={f} value={f} />)}</datalist>
          </div>
          <ImmoButton art="akzent" groesse={48} pille={false} icon={Plus} disabled={!gewaehlt || laeuft} onClick={() => { void verknuepfen(); }}>Verknüpfen</ImmoButton>
        </div>
      </InnenKarte>

      {loesen && (
        <BestaetigenDialog titel="Kontakt lösen?" text="Die Verknüpfung zum Objekt wird entfernt, der Kontakt selbst bleibt erhalten." detail={kontaktName(loesen)}
          bestaetigenLabel="Lösen" laeuft={laeuft} onBestaetigen={() => { void bestaetigeLoesen(); }} onAbbrechen={() => setLoesen(null)} />
      )}
    </>
  );
}
